import { client } from "./index";
import type { Headline } from "@/widget-components/newsquawk-headlines/models/Headline";
import type {
  SearchHeadlinesParams,
  SearchResponse,
} from "@/widget-components/newsquawk-headlines/api/types";
import { transformSearchParams } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import type {
  GetHeadlinesParams,
} from "./types";

export const endpoints = {
  Headlines: "/headlines/",
  Poll: "/headlines/poll",
  HeadlineById: (id: number) => `/headlines/${id}`,
  SoftDelete: (id: number) => `/headlines/${id}/soft-delete`,
  CountTotal: "/headlines/count/total",
  ByTag: (tagId: number) => `/headlines/tags/${tagId}`,
  SearchFullText: "/headlines/search/full_text",
  DelayedHeadlines: "/public/delayed/headlines",
};

/**
 * GET /headlines/ - Read Headlines
 */
export const getHeadlines = async (
  params?: GetHeadlinesParams
): Promise<SearchResponse> => {
  const response = await client.get(endpoints.Headlines, { params });
  return response.data;
};

/**
 * GET /headlines/poll - Cursor-based polling for headlines
 * Server tracks what's new via cursor, handling deduplication server-side.
 */
export const pollHeadlines = async (
  params?: GetHeadlinesParams
): Promise<SearchResponse> => {
  const response = await client.get(endpoints.Poll, { params });
  return response.data;
};

export const getDelayedHeadlines = async (
  params?: GetHeadlinesParams
): Promise<SearchResponse> => {
  const response = await client.get(endpoints.DelayedHeadlines, { params });
  return response.data;
};

/**
 * GET /headlines/{headline_id} - Read Headline
 */
export const getHeadlineById = async (
  headlineId: number
): Promise<Headline> => {
  const response = await client.get(endpoints.HeadlineById(headlineId));
  return response.data;
};

/**
 * POST /headlines/ - Create New Headline
 */
export const createHeadline = async (
  headlineData: Partial<Headline>
): Promise<Headline> => {
  const response = await client.post(endpoints.Headlines, headlineData);
  return response.data;
};

/**
 * PATCH /headlines/{headline_id} - Update Existing Headline
 */
export const updateHeadline = async (
  headlineData: Partial<Headline> & { id: number }
): Promise<Headline> => {
  const { id, ...data } = headlineData;
  const response = await client.patch(endpoints.HeadlineById(id), data);
  return response.data;
};

/**
 * DELETE /headlines/{headline_id} - Delete Existing Headline
 */
export const deleteHeadline = async (headlineId: number): Promise<void> => {
  await client.delete(endpoints.HeadlineById(headlineId));
};

/**
 * POST /headlines/{headline_id}/soft-delete - Soft Delete Existing Headline
 */
export const softDeleteHeadline = async (headlineId: number): Promise<void> => {
  await client.post(endpoints.SoftDelete(headlineId));
};

/**
 * GET /headlines/count/total - Count Headlines
 */
export const countHeadlines = async (): Promise<number> => {
  const response = await client.get(endpoints.CountTotal);
  return response.data;
};

/**
 * GET /headlines/tags/{tag_id} - Read Headlines By Tag
 */
export const getHeadlinesByTag = async (tagId: number): Promise<Headline[]> => {
  const response = await client.get(endpoints.ByTag(tagId));
  return response.data;
};

/**
 * POST /headlines/search/full_text - Search Full Text Headlines
 */
export const searchFullTextHeadlines = async (
  params: SearchHeadlinesParams
): Promise<SearchResponse> => {
  const payload = transformSearchParams(params);
  const response = await client.post(endpoints.SearchFullText, payload, {
    signal: params.signal,
  });
  return response.data;
};

